import { useMutation } from '@tanstack/react-query'
import type { SubmitEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import type { UserResponse } from '@/api/responses'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
} from '@/components/ui'

import { login } from './api'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface FieldErrors {
  email?: string
  password?: string
}

export interface LoginPageProps {
  readonly onSuccess: (user: UserResponse) => void
  readonly onNavigateToSignup: () => void
  readonly onNavigateToForgotPassword: () => void
}

export function LoginPage({
  onSuccess,
  onNavigateToSignup,
  onNavigateToForgotPassword,
}: LoginPageProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const mutation = useMutation({
    mutationFn: login,
    onSuccess,
  })

  function validate(): FieldErrors {
    const errors: FieldErrors = {}

    if (!EMAIL_PATTERN.test(email)) {
      errors.email = t('auth.login.errors.emailRequired')
    }

    if (password.length === 0) {
      errors.password = t('auth.login.errors.passwordRequired')
    }

    return errors
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault()
    const errors = validate()
    setFieldErrors(errors)

    if (Object.keys(errors).length > 0) {
      return
    }

    mutation.mutate({ email, password })
  }

  const apiMessages = describeApiError(mutation.error)
  const [firstMessage, ...restMessages] = apiMessages

  return (
    <main className="flex min-h-full items-center justify-center p-8">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader>
            <CardTitle>{t('auth.login.title')}</CardTitle>
            <CardDescription>{t('auth.login.subtitle')}</CardDescription>
          </CardHeader>
          <form
            onSubmit={(event) => {
              handleSubmit(event)
            }}
            noValidate
          >
            <CardContent className="flex flex-col gap-4">
              {firstMessage !== undefined ? (
                <Alert variant="danger" title={firstMessage}>
                  {restMessages.length > 0 ? (
                    <ul className="list-disc pl-4">
                      {restMessages.map((message) => (
                        <li key={message}>{message}</li>
                      ))}
                    </ul>
                  ) : null}
                </Alert>
              ) : null}
              <Input
                id="login-email"
                type="email"
                label={t('auth.login.emailLabel')}
                autoComplete="email"
                required
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value)
                }}
                error={fieldErrors.email}
              />
              <Input
                id="login-password"
                type="password"
                label={t('auth.login.passwordLabel')}
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value)
                }}
                error={fieldErrors.password}
              />
              <button
                type="button"
                onClick={onNavigateToForgotPassword}
                className="self-end text-sm font-medium text-action-primary hover:underline"
              >
                {t('auth.login.forgotLink')}
              </button>
            </CardContent>
            <CardFooter className="flex-col items-stretch gap-3">
              <Button type="submit" loading={mutation.isPending} className="w-full">
                {mutation.isPending ? t('auth.login.submitting') : t('auth.login.submit')}
              </Button>
              <p className="text-center text-sm text-foreground-muted">
                {t('auth.login.signupPrompt')}{' '}
                <button
                  type="button"
                  onClick={onNavigateToSignup}
                  className="font-medium text-action-primary hover:underline"
                >
                  {t('auth.login.signupLink')}
                </button>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>
    </main>
  )
}
